import { useState } from "react";
import { Briefcase, ArrowRight } from "react-feather";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Header, Content, Main } from "../commons/Template/Template";
import { Form } from "../commons/Form/Form";
import api from "../../api/resources";
import routes from "../../routes/routes";
import { useToastsDispatch } from "../commons/Toasts/ToastsContext";
import Address from "../commons/Address/Address";
import { useCountries } from "../../hooks/commons/useCountries";
import {
  FancyButtons,
  FancyButton,
} from "../commons/FancyButtons/FancyButtons";
import "./CompanyDetails.css";
import Typo, { Em } from "../commons/Typo/Typo";

export default function CompanyDetails() {
  const { i18n, t } = useTranslation(["common", "company"]);
  document.title = t("company:details.title");
  const dispatchToast = useToastsDispatch();
  const [company, setCompany] = useState(null);
  const [initialCompany, setInitialCompany] = useState(null);
  const [manualFill, setManualFill] = useState(false);
  const [results, setResults] = useState(null);
  const countries = useCountries();

  const handleManualFill = (notify = false, key = "") => {
    setManualFill(true);
    if (notify) {
      dispatchToast({
        type: "add",
        variant: "info",
        heading: t("company:details.search.form.message.title"),
        subheading: t("company:details.search.form.message.content"),
        className: "CompanyAutoSearchUnavailable",
      });
    }
    setCompany({
      name: parseInt(key) ? "" : key,
      code: parseInt(key) ? key : "",
      activity: "",
      immatriculation_date: "",
      address: {
        line_1: "",
        line_2: "",
        line_3: "",
        code: "",
        town: "",
        country: null,
      },
      is_reseller: null,
      is_ecommerce: false,
    });
  };

  const handleSelectResult = (company) => {
    const convertedCompany = {
      ...company,
      // Convert country code into label/value object for "select" component
      address: {
        ...company.address,
        country: {
          label: countries.getName(
            company.address.country,
            i18n.resolvedLanguage,
          ),
          value: company.address.country,
        },
      },
    };
    setCompany(convertedCompany);
    setInitialCompany(convertedCompany);
  };

  const handleSearchSuccess = (newResults) => setResults(newResults);

  const handleCompanyChange = (newValue, targetField, isAddress = false) =>
    isAddress
      ? setCompany((prev) => ({
          ...prev,
          address: {
            ...prev.address,
            [targetField]: newValue,
          },
        }))
      : setCompany((prev) => ({
          ...prev,
          [targetField]: newValue,
        }));

  if (company)
    return (
      <Verification
        initialCompany={initialCompany}
        company={company}
        onChange={handleCompanyChange}
        manualFill={manualFill}
        onClear={() => {
          setCompany(null);
          setManualFill(false);
        }}
      />
    );

  if (results)
    return (
      <Results
        results={results}
        onClear={() => setResults(null)}
        onManualFill={handleManualFill}
        onSelect={handleSelectResult}
      />
    );

  return (
    <Search onSuccess={handleSearchSuccess} onManualFill={handleManualFill} />
  );
}

function Search({ onSuccess, onManualFill }) {
  const { t } = useTranslation(["common", "company"]);
  const [term, setTerm] = useState("");
  const [previousTerm, setPreviousTerm] = useState("");
  const [searchSuccess, setSearchSuccess] = useState(true);

  const handleSearch = async () => {
    setSearchSuccess(true);
    try {
      const response = await api.searchCompanies({
        key: term.replace(/\s+/g, ""),
      });
      if (response.ok) {
        const companies = await response.json();
        if (companies.length === 0) {
          setPreviousTerm(term);
          setSearchSuccess(false);
        } else {
          onSuccess(companies);
        }
      } else {
        onManualFill(true, term);
      }
    } catch (error) {
      console.log(error);
      onManualFill(true, term);
    }
  };

  // SIREN
  const termInput = [
    {
      label: t("company:details.search.form.label"),
      placeholder: "123456789",
      type: "text",
      name: "term",
      value: term,
      pattern: "[0-9\\s]+",
      onChange: setTerm,
    },
  ];

  return (
    <Main>
      <Header>
        <Typo variant="h1">
          <Trans
            t={t}
            i18nKey={"company:details.search.heading"}
            components={{
              emphasis: <Em />,
            }}
          />
        </Typo>
        {!searchSuccess && (
          <Typo variant="body2">
            <Trans
              t={t}
              i18nKey={"company:details.search.subheading"}
              values={{ searchTerm: previousTerm }}
              components={{
                anchor: (
                  <Link
                    className="CompanyManualFill"
                    onClick={() => onManualFill(false)}
                  />
                ),
                emphasis: <strong />,
              }}
            />
          </Typo>
        )}
      </Header>
      <Content>
        <Form
          resetLoader={!searchSuccess}
          onSubmit={handleSearch}
          button={t("company:details.search.form.button")}
          inputs={[{ data: termInput }]}
        />
      </Content>
    </Main>
  );
}

function Results({ results, onClear, onManualFill, onSelect }) {
  const { t } = useTranslation(["common", "company"]);

  return (
    <Main themeSwitch style={{ alignItems: "center", width: "fit-content" }}>
      <Header
        onBackButtonClick={onClear}
        containerStyle={{ alignSelf: "flex-start" }}
      >
        <Typo variant="h1">
          <Trans
            t={t}
            i18nKey={"company:details.searchResults.heading"}
            components={{
              emphasis: <span />,
            }}
          />
        </Typo>
        <Typo variant="body2">
          <Trans
            t={t}
            i18nKey={"company:details.searchResults.subheading"}
            components={{
              anchor: <Link onClick={() => onManualFill(false)} />,
            }}
          />
        </Typo>
      </Header>
      <Content>
        <CompanyResults results={results} onSelect={onSelect} />
      </Content>
    </Main>
  );
}

export function CompanyResults({ results, onSelect }) {
  return (
    <FancyButtons>
      {results.map((searchResult, index) => {
        return (
          <FancyButton
            key={index}
            heading={searchResult.name}
            subheading={
              searchResult.address && (
                <Address
                  address={searchResult.address}
                  country={searchResult.country}
                />
              )
            }
            icon={<Briefcase />}
            iconConf={<ArrowRight />}
            onClick={() => onSelect(searchResult)}
          />
        );
      })}
    </FancyButtons>
  );
}

function Verification({
  initialCompany,
  company,
  onChange,
  manualFill,
  onClear,
}) {
  const { i18n, t } = useTranslation(["common", "company", "account"]);
  const countries = useCountries();
  const dispatchToast = useToastsDispatch();

  const initErrors = (object) => ({
    ...Object.keys(object).reduce((prev, curr) => {
      return {
        ...prev,
        [curr]: [],
      };
    }, {}),
    non_field_errors: [],
  });

  const [errors, setErrors] = useState(initErrors(company));

  const clearErrors = () => {
    setErrors(initErrors(company));
  };

  const handleCreateCompany = async () => {
    clearErrors();

    const response = await api.createCompany({
      name: company.name,
      code: manualFill ? company.code : initialCompany.code,
      activity: manualFill ? company.activity : initialCompany.activity,
      immatriculation_date: manualFill
        ? company.immatriculation_date
        : initialCompany.immatriculation_date,
      address: {
        ...company.address,
        country: manualFill
          ? company.address.country.value
          : initialCompany.address.country.value,
      },
      is_reseller: company.is_reseller.value,
      is_ecommerce: company.is_ecommerce,
      is_commercial: company.is_commercial,
      turnover: company.turnover,
      equity: company.equity,
      gross_operating_profit: company.gross_operating_profit,
      net_income: company.net_income,
      has_insolvency_proceeding: company.has_insolvency_proceeding,
      proceedings_dates: company.proceedings_dates,
    });

    if (response.ok) {
      if (!manualFill) {
        const { representants } = company;
        for (const rep of representants) {
          await api.createRep(rep);
        }
      }
      dispatchToast({
        type: "add",
        variant: "success",
        heading: t("company:details.verifyCompany.form.message.create.title"),
        subheading: t(
          "company:details.verifyCompany.form.message.create.content",
        ),
      });
      window.location.href = routes.Company.reps; // not using navigate because app needs refresh (active company is switched on backend after creation)
    } else {
      const jsonErrors = await response.json();
      setErrors(jsonErrors);
    }
  };

  const nameInput = {
    label: t("company:details.verifyCompany.form.name"),
    type: "text",
    name: "name",
    value: company.name,
    onChange: (value) => onChange(value, "name"),
    errors: errors.name,
  };

  const dateInput = {
    label: t("company:details.verifyCompany.form.date"),
    type: "date",
    name: "date",
    value: company.immatriculation_date,
    onChange: (value) => onChange(value, "immatriculation_date"),
    disabled: !manualFill,
    errors: errors.immatriculation_date,
  };

  const codeInput = {
    label: t("company:details.verifyCompany.form.identifier"),
    type: "text",
    name: "code",
    value: company.code,
    onChange: (value) => onChange(value, "code"),
    disabled: !manualFill,
    errors: errors.code,
  };

  const activityInput = {
    label: t("company:details.verifyCompany.form.activity_code"),
    type: "text",
    name: "activity",
    value: company.activity,
    onChange: (value) => onChange(value, "activity"),
    disabled: !manualFill,
    errors: errors.activity,
  };

  const addressLine1Input = {
    label: t("company:details.verifyCompany.form.address.title"),
    placeholder: t("company:details.verifyCompany.form.address.line_1"),
    type: "text",
    name: "street",
    value: company.address.line_1,
    onChange: (value) => onChange(value, "line_1", true),
    errors: errors.address,
  };

  const addressLine2Input = {
    placeholder: t("company:details.verifyCompany.form.address.line_2"),
    required: false,
    type: "text",
    name: "complement",
    value: company.address.line_2,
    onChange: (value) => onChange(value, "line_2", true),
  };

  const addressCodeInput = {
    placeholder: t("company:details.verifyCompany.form.address.code"),
    type: "text",
    name: "postalCode",
    value: company.address.code,
    onChange: (value) => onChange(value, "code", true),
  };

  const addressTownInput = {
    placeholder: t("company:details.verifyCompany.form.address.town"),
    type: "text",
    name: "town",
    value: company.address.town,
    onChange: (value) => onChange(value, "town", true),
  };

  const countriesOptions = Object.entries(
    countries.getNames(i18n.resolvedLanguage, { select: "official" }),
  ).map(([alpha2Code, name]) => {
    return {
      label: name,
      value: alpha2Code,
    };
  });

  const countryInput = {
    type: "select",
    name: "country",
    value: company.address.country,
    onChange: (value) => onChange(value, "country", true),
    options: countriesOptions,
    placeholder: company.address.country
      ? countries.getName(company.address.country, i18n.resolvedLanguage)
      : t("company:details.verifyCompany.form.address.country"),
    disabled: !manualFill,
    required: true,
  };

  const isResellerOptions = [
    {
      label: t("account:signup.form.reseller.option.manager"),
      value: false,
      className: "manager",
    },
    {
      label: t("account:signup.form.reseller.option.reseller"),
      value: true,
      className: "reseller",
    },
  ];

  const isResellerInput = {
    label: t("account:signup.form.reseller.label"),
    type: "select",
    name: "is_reseller",
    options: isResellerOptions,
    value: company.is_reseller,
    onChange: (value) => onChange(value, "is_reseller"),
    required: true,
    autoSortOptionsDisabled: true,
  };

  const ecomInput = {
    type: "checkbox",
    name: "is_ecommerce",
    value: company.is_ecommerce,
    onChange: (value) => onChange(value, "is_ecommerce"),
    label: t("company:details.verifyCompany.form.ecom"),
    required: false,
  };

  const inputs = [
    { data: [nameInput, dateInput], display: "grid" },
    { data: [codeInput, activityInput], display: "grid" },
    { data: [addressLine1Input] },
    { data: [addressLine2Input] },
    { data: [addressCodeInput, addressTownInput], display: "grid" },
    { data: [countryInput] },
    { data: [isResellerInput] },
    { data: [ecomInput] },
  ];

  return (
    <Main themeSwitch style={{ alignItems: "center", width: "fit-content" }}>
      <Header
        onBackButtonClick={onClear}
        containerStyle={{ alignSelf: "flex-start" }}
      >
        <Typo variant="h1">
          {company.name || (
            <Trans
              t={t}
              i18nKey={"company:details.verifyCompany.heading"}
              components={{
                emphasis: <span />,
              }}
            />
          )}
        </Typo>
      </Header>
      <Content style={{ maxWidth: "35rem" }}>
        <Form
          className={"CompanyDetailsForm"}
          button={t("company:details.verifyCompany.form.button")}
          onSubmit={handleCreateCompany}
          errors={errors.non_field_errors}
          inputs={inputs}
        />
      </Content>
    </Main>
  );
}
